import React from 'react';

import { calculateLeverage } from '#/api/apiUtils';
import DataPair from '#/components/basic/panels/DataPair';
import {
  getMarketTradingHoursLabel,
  isIntermittentMarket,
} from '#/components/markets/intermittent/intermittentMarkets';
import MarketSessionInfoTooltip from '#/components/markets/intermittent/MarketSessionInfoTooltip';
import { useIntermittentMarketSessionDisplay } from '#/components/markets/intermittent/useIntermittentMarketSessionDisplay';
import { CoinIcon } from '#/config/coinIcons/CoinIcon';
import { formatDateTime } from '#/lib/date';
import {
  buildMarketPair,
  formatNumber,
  formatNumber2,
} from '#/lib/display/formatting';
import {
  useSelectMarketData,
  useSelectMarketStepSize,
} from '#/reduxState/markets/listed/listedMarketsUtils';

function MarketDetailsWidget({ base, quote }: { base: string; quote: string }) {
  const marketData = useSelectMarketData(buildMarketPair(base, quote));

  const marketStepSize = useSelectMarketStepSize(buildMarketPair(base, quote));
  const session = useIntermittentMarketSessionDisplay(marketData, 'every-tick');

  const quantityDecimals = marketStepSize?.visibleDecimals;

  if (!marketData || quantityDecimals === undefined) {
    return null;
  }

  const coinIcon = <CoinIcon symbol={marketData.baseAsset} size="12px" />;
  const isIntermittent = isIntermittentMarket(marketData.type);

  const items = [
    ['Schedule', getMarketTradingHoursLabel(marketData.type) ?? 'Continuous'],
    ['Status', session?.isClosedForTrading ? 'Closed' : 'Open'],
    ...(isIntermittent
      ? [
          [
            'Next Close',
            <>
              {marketData.approximateNextCloseTime
                ? formatDateTime(marketData.approximateNextCloseTime)
                : '-'}{' '}
              <MarketSessionInfoTooltip />
            </>,
          ],
          [
            'Next Open',
            <>
              {marketData.approximateNextOpenTime
                ? formatDateTime(marketData.approximateNextOpenTime)
                : '-'}{' '}
              <MarketSessionInfoTooltip />
            </>,
          ],
        ]
      : []),
    [
      'Margin Type',
      marketData.marginType === 'isolated' ? 'Isolated' : 'Cross',
    ],
    ['Tick Size', <>${formatNumber2(marketData.tickSize)}</>],
    [
      'Step Size',
      <>
        {coinIcon} {formatNumber2(marketData.stepSize)}
      </>,
    ],
    [
      'Minimum Order Size (Taker)',
      <>
        {coinIcon}{' '}
        {formatNumber(marketData.takerOrderMinimum, {
          fixedDecimals: quantityDecimals,
        })}
      </>,
    ],
    [
      'Minimum Order Size (Maker)',
      <>
        {coinIcon}{' '}
        {formatNumber(marketData.makerOrderMinimum, {
          fixedDecimals: quantityDecimals,
        })}
      </>,
    ],
    [
      'Maximum Leverage',
      marketData.initialMarginFraction
        ? `${calculateLeverage(marketData.initialMarginFraction)}x`
        : '',
    ],
    [
      'Maintenance Margin Fraction',
      formatNumber(marketData.maintenanceMarginFraction, { fixedDecimals: 2 }),
    ],
    [
      'Initial Margin Fraction',
      formatNumber(marketData.initialMarginFraction, { fixedDecimals: 2 }),
    ],
    [
      'Incremental Initial Margin Fraction',
      formatNumber(marketData.incrementalInitialMarginFraction, {
        fixedDecimals: 2,
      }),
    ],
    [
      'Incremental Position Size',
      <>
        {coinIcon}{' '}
        {formatNumber(marketData.incrementalPositionSize, {
          fixedDecimals: quantityDecimals,
        })}
      </>,
    ],
    [
      'Baseline Position Size',
      <>
        {coinIcon}{' '}
        {formatNumber(marketData.basePositionSize, {
          fixedDecimals: quantityDecimals,
        })}
      </>,
    ],
    [
      'Minimum Position Size',
      <>
        {coinIcon}{' '}
        {formatNumber(marketData.minimumPositionSize, {
          fixedDecimals: quantityDecimals,
        })}
      </>,
    ],
    [
      'Maximum Position Size',
      <>
        {coinIcon}{' '}
        {formatNumber(marketData.maximumPositionSize, {
          fixedDecimals: quantityDecimals,
        })}
      </>,
    ],
  ];
  return (
    <div>
      {items.map(([label, value], index) => (
        <DataPair
          key={label.toString() + index}
          label={label}
          altBackground={(index + 1) % 2 === 0}
          labelWidth="210px"
        >
          {value}
        </DataPair>
      ))}
    </div>
  );
}

export default MarketDetailsWidget;
